import { END, eventChannel, EventChannel } from 'redux-saga';

type UploadBody = Blob | File | FormData | string | { uri: string };

export type RequestProgressEvent =
  | { type: 'progress'; loaded: number; total: number }
  | { type: 'done' }
  | { type: 'error'; error: Error };

type CreateRequestProgressChannelOptions = {
  body: UploadBody;
  errorMessage: string;
  total?: number;
  abortErrorMessage?: string;
  headers?: Record<string, string>;
  method?: 'POST' | 'PUT' | 'PATCH';
  url: string;
};

const getBodySize = (body: UploadBody): number =>
  typeof body === 'object' && 'size' in body && typeof body.size === 'number'
    ? body.size
    : 0;

export const createRequestProgressChannel = ({
  body,
  errorMessage,
  total,
  abortErrorMessage = 'Request was cancelled',
  headers,
  method = 'PUT',
  url,
}: CreateRequestProgressChannelOptions): EventChannel<RequestProgressEvent> =>
  eventChannel<RequestProgressEvent>((emit) => {
    const request = new XMLHttpRequest();
    const totalFallback = total ?? getBodySize(body);
    let isComplete = false;

    request.upload.onprogress = (event) => {
      emit({
        type: 'progress',
        loaded: event.loaded,
        total: event.lengthComputable ? event.total : totalFallback,
      });
    };

    request.onload = () => {
      isComplete = true;
      if (request.status >= 200 && request.status < 300) {
        emit({
          type: 'progress',
          loaded: totalFallback,
          total: totalFallback,
        });
        emit({ type: 'done' });
        emit(END);
        return;
      }

      emit({
        type: 'error',
        error: new Error(errorMessage),
      });
      emit(END);
    };

    request.onerror = () => {
      isComplete = true;
      emit({
        type: 'error',
        error: new Error(errorMessage),
      });
      emit(END);
    };

    request.onabort = () => {
      isComplete = true;
      emit({
        type: 'error',
        error: new Error(abortErrorMessage),
      });
      emit(END);
    };

    request.open(method, url);
    Object.entries(headers || {}).forEach(([name, value]) => {
      request.setRequestHeader(name, value);
    });
    request.send(body);

    return () => {
      if (!isComplete) {
        request.abort();
      }
    };
  });
